import "core-js/modules/es.array.reverse.js";
var _class;
import "core-js/modules/es.array.sort.js";
import "core-js/modules/web.dom-collections.iterator.js";
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
import i18next from "i18next";
import { computed, runInAction, makeObservable, override } from "mobx";
import RequestErrorEvent from "terriajs-cesium/Source/Core/RequestErrorEvent";
import Resource from "terriajs-cesium/Source/Core/Resource";
import filterOutUndefined from "../../../Core/filterOutUndefined";
import isDefined from "../../../Core/isDefined";
import TerriaError, { TerriaErrorSeverity } from "../../../Core/TerriaError";
import TableMixin from "../../../ModelMixins/TableMixin";
import UrlMixin from "../../../ModelMixins/UrlMixin";
import Csv from "../../../Table/Csv";
import TableAutomaticStylesStratum from "../../../Table/TableAutomaticStylesStratum";
import SdmxCatalogItemTraits from "../../../Traits/TraitsClasses/SdmxCatalogItemTraits";
import CreateModel from "../../Definition/CreateModel";
import StratumOrder from "../../Definition/StratumOrder";
import { filterEnums } from "../../SelectableDimensions/SelectableDimensions";
import proxyCatalogItemUrl from "../proxyCatalogItemUrl";
import { SdmxJsonDataflowStratum } from "./SdmxJsonDataflowStratum";
import { sdmxErrorString, SdmxHttpErrorCodes } from "./SdmxJsonServerStratum";
let SdmxJsonCatalogItem = (_class = class SdmxJsonCatalogItem extends TableMixin(UrlMixin(CreateModel(SdmxCatalogItemTraits))) {
  static get type() {
    return "sdmx-json";
  }
  constructor(id, terria, sourceReference) {
    super(id, terria, sourceReference);
    makeObservable(this);
    this.strata.set(TableAutomaticStylesStratum.stratumName, new TableAutomaticStylesStratum(this));
  }
  async forceLoadMetadata() {
    // Load SdmxJsonDataflowStratum if needed
    if (!this.strata.has(SdmxJsonDataflowStratum.stratumName)) {
      const stratum = await SdmxJsonDataflowStratum.load(this);
      runInAction(() => {
        this.strata.set(SdmxJsonDataflowStratum.stratumName, stratum);
      });
    }
    await this.loadRegionProviderList();
  }
  get type() {
    return SdmxJsonCatalogItem.type;
  }
  get cacheDuration() {
    return super.cacheDuration || "1d";
  }

  /**
   * Map SdmxDimensionTraits to SelectableDimension
   */
  get sdmxSelectableDimensions() {
    return this.dimensions.map(dim => {
      var _this$columns$find;
      return {
        id: dim.id,
        name: dim.name,
        options: dim.options,
        selectedId: dim.selectedId,
        allowUndefined: dim.allowUndefined,
        disable: dim.disable || ((_this$columns$find = this.columns.find(col => col.name === dim.id)) === null || _this$columns$find === void 0 ? void 0 : _this$columns$find.type) === "region",
        setDimensionValue: async (stratumId, value) => {
          var _this$dimensions;
          let dimensionTraits = (_this$dimensions = this.dimensions) === null || _this$dimensions === void 0 ? void 0 : _this$dimensions.find(sdmxDim => sdmxDim.id === dim.id);
          if (!isDefined(dimensionTraits)) {
            dimensionTraits = this.addObject(stratumId, "dimensions", dim.id);
          }
          dimensionTraits.setTrait(stratumId, "selectedId", value);
          (await this.loadMapItems()).raiseError(this.terria);
        }
      };
    });
  }
  get selectableDimensions() {
    return filterOutUndefined([...super.selectableDimensions.filter(d => {
      var _this$styleDimensions;
      return d.id !== ((_this$styleDimensions = this.styleDimensions) === null || _this$styleDimensions === void 0 ? void 0 : _this$styleDimensions.id);
    }), ...this.sdmxSelectableDimensions]);
  }

  /**
   * Returns base URL (from traits), as SdmxJsonCatalogItem will override `url` property with SDMX Data request
   */
  get baseUrl() {
    return super.url;
  }
  get url() {
    if (!super.url) return;

    // Get dataKey - this is used to filter dataflows by dimension values - it must be compliant with the KeyType defined in the SDMX WADL (period separated dimension values) - dimension order is very important!
    // We must sort the dimensions by position as traits lose their order across strata

    const dataKey = this.dimensions.slice().sort((a, b) => (isDefined(a.position) ? a.position : this.dimensions.length) - (isDefined(b.position) ? b.position : this.dimensions.length))
    // If a dimension is disabled, use empty string (which is wildcard)
    .map(dim => {
      var _this$columns$find2;
      return !dim.disable && ((_this$columns$find2 = this.columns.find(col => col.name === dim.id)) === null || _this$columns$find2 === void 0 ? void 0 : _this$columns$find2.type) !== "region" ? dim.selectedId : "";
    }).join(".");
    return "".concat(super.url, "/data/").concat(this.dataflowId, "/").concat(dataKey);
  }
  async forceLoadTableData() {
    if (!this.url) return;
    try {
      const csvString = await new Resource({
        url: proxyCatalogItemUrl(this, this.url),
        headers: {
          Accept: "application/vnd.sdmx.data+csv; version=1.0.0"
        }
      }).fetch();
      if (!isDefined(csvString)) {
        throw new TerriaError({
          title: i18next.t("models.sdmxCatalogItem.loadDataErrorTitle"),
          message: i18next.t("models.sdmxCatalogItem.loadDataErrorMessage", this)
        });
      }
      return await Csv.parseString(csvString, true);
    } catch (error) {
      if (error instanceof RequestErrorEvent && typeof error.response === "string") {
        // If no results and we have selcetable dimensions, give message regarding dimensions
        // This message will include values for each selectable dimension
        if (error.statusCode === SdmxHttpErrorCodes.NoResults && this.selectableDimensions.length > 0) {
          throw new TerriaError({
            message: i18next.t("models.sdmxCatalogItem.noResultsWithDimensions", {
              dimensions: filterEnums(this.selectableDimensions).filter(dim => {
                var _dim$options;
                return !dim.disable && ((_dim$options = dim.options) === null || _dim$options === void 0 ? void 0 : _dim$options.length) !== 1;
              }).map(dim => {
                var _dim$options$find$nam, _dim$options2;
                return (// Format string into `${dimenion name} = ${dimenion selected value}
                  "- ".concat(dim.name, " = `").concat((_dim$options$find$nam = (_dim$options2 = dim.options) === null || _dim$options2 === void 0 || (_dim$options2 = _dim$options2.find(option => option.id === dim.selectedId)) === null || _dim$options2 === void 0 ? void 0 : _dim$options2.name) !== null && _dim$options$find$nam !== void 0 ? _dim$options$find$nam : dim.selectedId, "`")
                );
              }).join("\n")
            }),
            title: i18next.t("models.sdmxCatalogItem.loadDataErrorTitle", this),
            severity: TerriaErrorSeverity.Warning,
            importance: 1,
            overrideRaiseToUser: true
          });
        }
        throw new TerriaError({
          message: sdmxErrorString.has(error.statusCode) ? "".concat(sdmxErrorString.get(error.statusCode), ": ").concat(error.response) : "".concat(error.response),
          title: i18next.t("models.sdmxCatalogItem.loadDataErrorTitle", this)
        });
      } else {
        throw TerriaError.from(error, {
          message: i18next.t("models.sdmxCatalogItem.loadDataErrorTitle", this)
        });
      }
    }
  }
}, _applyDecoratedDescriptor(_class.prototype, "cacheDuration", [override], Object.getOwnPropertyDescriptor(_class.prototype, "cacheDuration"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "sdmxSelectableDimensions", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "sdmxSelectableDimensions"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectableDimensions", [override], Object.getOwnPropertyDescriptor(_class.prototype, "selectableDimensions"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "baseUrl", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "baseUrl"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "url", [override], Object.getOwnPropertyDescriptor(_class.prototype, "url"), _class.prototype), _class);
export { SdmxJsonCatalogItem as default };
StratumOrder.addLoadStratum(TableAutomaticStylesStratum.stratumName);